import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { checkRole, userProfile } from 'utils/auth';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import { hasDealerContext } from 'utils/hasDealerContext';
import type {
  HilErrorCategory,
  HilOpportunityType,
  HumanInTheLoopSection,
} from 'app/pages/HumanInTheLoopPage/types';
import {
  buildHumanInTheLoopBreadcrumbs,
  buildHumanInTheLoopNavigationState,
  buildHumanInTheLoopPath,
  getHumanInTheLoopSectionLabel,
  parseHilOpportunityTypeFromSearch,
  parseHumanInTheLoopViewFromSearch,
  parseSegmentNameFromSearch,
} from 'app/pages/HumanInTheLoopPage/utils/humanInTheLoopRouteUtils';
import type {
  BuildHumanInTheLoopPathOptions,
  HumanInTheLoopBreadcrumbState,
} from 'app/pages/HumanInTheLoopPage/utils/humanInTheLoopRouteUtils';
import DataGridTypes from 'types/DataGridTypes';

const HIL_PAGE_ROLES = ['super_user', 'maiq_super_admin', 'maiq_tech_user'];

function normalizeRoles(roles: unknown): string[] {
  if (Array.isArray(roles)) return roles as string[];
  if (typeof roles === 'string' && roles.trim()) return [roles];
  return [];
}

const HIL_TEMPLATES_ENDPOINT = 'api/v2/templates';
const HIL_SEGMENTS_ATTENTION_ENDPOINT =
  'api/v2/nurturing-automation/automation-needing-attention';

function parseTotalDocumentsFromResponse(response: unknown): number {
  const totalDocuments = (response as { body?: { meta?: { totalDocuments?: unknown } } })
    ?.body?.meta?.totalDocuments;
  const numeric = Number(totalDocuments);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

export function useHumanInTheLoopPage() {
  const history = useHistory();
  const location = useLocation<HumanInTheLoopBreadcrumbState>();
  const user = userProfile();
  const canAccess = checkRole(normalizeRoles(user?.roles), HIL_PAGE_ROLES);

  const selectedDealer = useSelector(selectDealerSelected);
  const hasSelectedDealer = hasDealerContext(selectedDealer);
  const hilPageLabel = hasSelectedDealer
    ? 'HIL'
    : 'Human in the loop review';

  const activeSection = useMemo(
    () => parseHumanInTheLoopViewFromSearch(location.search),
    [location.search],
  );

  const opportunityType = useMemo(
    () => parseHilOpportunityTypeFromSearch(location.search),
    [location.search],
  );

  const segmentNameFromSearch = useMemo(
    () => parseSegmentNameFromSearch(location.search),
    [location.search],
  );

  const pathOptionsForSection = useMemo((): BuildHumanInTheLoopPathOptions => {
    const options: BuildHumanInTheLoopPathOptions = {
      opportunityType,
    };
    if (activeSection === 'templates_pending' && segmentNameFromSearch) {
      options.segmentName = segmentNameFromSearch;
    }
    return options;
  }, [activeSection, opportunityType, segmentNameFromSearch]);

  const breadcrumbsList = useMemo(
    () =>
      buildHumanInTheLoopBreadcrumbs(
        activeSection,
        location.state,
        pathOptionsForSection,
        { hilLabel: hilPageLabel },
      ),
    [activeSection, hilPageLabel, location.state, pathOptionsForSection],
  );

  const humanInTheLoopPath = useMemo(
    () => buildHumanInTheLoopPath(activeSection, pathOptionsForSection),
    [activeSection, pathOptionsForSection],
  );

  const setActiveSection = useCallback(
    (
      section: HumanInTheLoopSection,
      options?: BuildHumanInTheLoopPathOptions,
    ) => {
      const nextPath = buildHumanInTheLoopPath(section, {
        opportunityType,
        ...options,
      });
      const currentPath = `${location.pathname}${location.search}`;
      if (currentPath === nextPath) return;
      const nextState = buildHumanInTheLoopNavigationState(
        section,
        location.state,
        { opportunityType, ...options },
        { hilLabel: hilPageLabel, clearRedirectFromSection: true },
      );
      history.push(nextPath, nextState);
    },
    [history, hilPageLabel, location.pathname, location.search, location.state, opportunityType],
  );

  const setOpportunityType = useCallback(
    (next: HilOpportunityType) => {
      if (next === opportunityType) return;
      const nextPath = buildHumanInTheLoopPath(activeSection, {
        ...pathOptionsForSection,
        opportunityType: next,
      });
      const nextState = buildHumanInTheLoopNavigationState(
        activeSection,
        location.state,
        { ...pathOptionsForSection, opportunityType: next },
        { hilLabel: hilPageLabel },
      );
      history.push(nextPath, nextState);
    },
    [
      activeSection,
      hilPageLabel,
      history,
      location.state,
      opportunityType,
      pathOptionsForSection,
    ],
  );

  const selectedDealerId = selectedDealer?.dealer
    ? String(selectedDealer.dealer)
    : '';

  const { data: pendingTemplatesCountResponse } = useQuery({
    queryKey: ['hil-templates-pending-count', selectedDealerId],
    enabled: Boolean(selectedDealerId),
    queryFn: () =>
      fetchDataFromAPI(
        HIL_TEMPLATES_ENDPOINT,
        {
          dealer: selectedDealerId,
          status: 'PENDING_APPROVAL',
        },
        1,
        1,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: functionalSegmentsCountResponse } = useQuery({
    queryKey: [
      'hil-segments-attention-count',
      selectedDealerId,
      opportunityType,
      'functional',
    ],
    enabled: Boolean(selectedDealerId),
    queryFn: () =>
      fetchDataFromAPI(
        HIL_SEGMENTS_ATTENTION_ENDPOINT,
        {
          dealer_id: selectedDealerId,
          opportunity_type: opportunityType,
          error_category: 'functional',
        },
        1,
        1,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: systemSegmentsCountResponse } = useQuery({
    queryKey: [
      'hil-segments-attention-count',
      selectedDealerId,
      opportunityType,
      'system',
    ],
    enabled: Boolean(selectedDealerId),
    queryFn: () =>
      fetchDataFromAPI(
        HIL_SEGMENTS_ATTENTION_ENDPOINT,
        {
          dealer_id: selectedDealerId,
          opportunity_type: opportunityType,
          error_category: 'system',
        },
        1,
        1,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const pendingTemplatesCountFromApi = useMemo(
    () => parseTotalDocumentsFromResponse(pendingTemplatesCountResponse),
    [pendingTemplatesCountResponse],
  );

  const functionalSegmentsCountFromApi = useMemo(
    () => parseTotalDocumentsFromResponse(functionalSegmentsCountResponse),
    [functionalSegmentsCountResponse],
  );

  const systemSegmentsCountFromApi = useMemo(
    () => parseTotalDocumentsFromResponse(systemSegmentsCountResponse),
    [systemSegmentsCountResponse],
  );

  const [templatesPendingCountFromGrid, setTemplatesPendingCountFromGrid] =
    useState<number | null>(null);

  const [functionalSegmentsCountFromGrid, setFunctionalSegmentsCountFromGrid] =
    useState<number | null>(null);

  const [systemSegmentsCountFromGrid, setSystemSegmentsCountFromGrid] =
    useState<number | null>(null);

  const [segmentsAttentionGridRefreshKey, setSegmentsAttentionGridRefreshKey] =
    useState(0);

  const [segmentsErrorCategory, setSegmentsErrorCategory] =
    useState<HilErrorCategory>('functional');

  useEffect(() => {
    setTemplatesPendingCountFromGrid(null);
    setFunctionalSegmentsCountFromGrid(null);
    setSystemSegmentsCountFromGrid(null);
    setSegmentsAttentionGridRefreshKey(0);
    setSegmentsErrorCategory('functional');
  }, [selectedDealerId]);

  useEffect(() => {
    setFunctionalSegmentsCountFromGrid(null);
    setSystemSegmentsCountFromGrid(null);
    setSegmentsAttentionGridRefreshKey(0);
    setSegmentsErrorCategory('functional');
  }, [opportunityType]);

  const refreshSegmentsAttentionGrid = useCallback(() => {
    setSegmentsAttentionGridRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    setTemplatesPendingCountFromGrid(null);
  }, [segmentNameFromSearch]);

  const handleTemplatesPendingTotalDocuments = useCallback(
    (totalDocuments: number) => {
      const numeric = Number(totalDocuments);
      if (!Number.isFinite(numeric) || numeric < 0) return;
      setTemplatesPendingCountFromGrid(numeric);
    },
    [],
  );

  /** Bootstrap from count APIs; active tab grid refines that category when loaded. */
  const pendingTemplatesCount =
    activeSection === 'templates_pending' &&
    templatesPendingCountFromGrid !== null
      ? templatesPendingCountFromGrid
      : pendingTemplatesCountFromApi;

  const functionalSegmentsCount =
    functionalSegmentsCountFromGrid !== null
      ? functionalSegmentsCountFromGrid
      : functionalSegmentsCountFromApi;

  const systemSegmentsCount =
    systemSegmentsCountFromGrid !== null
      ? systemSegmentsCountFromGrid
      : systemSegmentsCountFromApi;

  const segmentsAttentionCount = functionalSegmentsCount + systemSegmentsCount;

  const handleFunctionalSegmentsAttentionTotalDocuments = useCallback(
    (totalDocuments: number) => {
      const numeric = Number(totalDocuments);
      if (!Number.isFinite(numeric) || numeric < 0) return;
      setFunctionalSegmentsCountFromGrid(numeric);
    },
    [],
  );

  const handleSystemSegmentsAttentionTotalDocuments = useCallback(
    (totalDocuments: number) => {
      const numeric = Number(totalDocuments);
      if (!Number.isFinite(numeric) || numeric < 0) return;
      setSystemSegmentsCountFromGrid(numeric);
    },
    [],
  );

  const functionalSegmentsAttentionGridType = useMemo(() => {
    if (!selectedDealerId) return null;
    return DataGridTypes.HIL_SEGMENTS_NEEDING_ATTENTION(
      selectedDealerId,
      opportunityType,
      'functional',
    );
  }, [opportunityType, selectedDealerId]);

  const systemSegmentsAttentionGridType = useMemo(() => {
    if (!selectedDealerId) return null;
    return DataGridTypes.HIL_SEGMENTS_NEEDING_ATTENTION(
      selectedDealerId,
      opportunityType,
      'system',
    );
  }, [opportunityType, selectedDealerId]);

  const sectionHeading = getHumanInTheLoopSectionLabel(activeSection);

  const selectTemplatesSection = useCallback(() => {
    setActiveSection('templates_pending');
  }, [setActiveSection]);

  const selectSegmentsSection = useCallback(() => {
    setActiveSection('segments_attention');
  }, [setActiveSection]);

  return {
    canAccess,
    hasSelectedDealer,
    selectedDealerId,
    breadcrumbsList,
    humanInTheLoopPath,
    activeSection,
    opportunityType,
    setOpportunityType,
    selectTemplatesSection,
    selectSegmentsSection,
    sectionHeading,
    counts: {
      templatesPending: pendingTemplatesCount,
      segmentsAttention: segmentsAttentionCount,
      functionalSegmentsAttention: functionalSegmentsCount,
    },
    segmentsErrorCategory,
    setSegmentsErrorCategory,
    functionalSegmentsAttentionGridType,
    systemSegmentsAttentionGridType,
    segmentsAttentionGridRefreshKey,
    refreshSegmentsAttentionGrid,
    handleFunctionalSegmentsAttentionTotalDocuments,
    handleSystemSegmentsAttentionTotalDocuments,
    handleTemplatesPendingTotalDocuments,
  };
}
